<template>
  <div class="dv-search">
    <section class="ai-page-head">
      <div class="ai-page-head__crumb">
        <router-link to="/data-visualization">시각화</router-link>
        <span>›</span>
        <em>데이터 기반 검색</em>
      </div>
      <h2 class="ai-page-head__title">데이터 기반 검색</h2>
      <p class="ai-page-head__sub">
        왼쪽 트리에서 <b>재료 · 공정 · 성능 · 분석</b> 아래 조건을 체크하면 조건에 맞는 실험 행만 남습니다.
        상위 항목의 체크박스는 하위 전체를 한 번에 선택·해제합니다. 결과 표와 출력 특성도 같은 분류로 묶여 보입니다.
      </p>
      <div class="ai-data-banner">
        <b>허브 실험입력 데이터</b>(작업본 최신) 기반입니다. 값이 없는 조건·열은 표시되지 않습니다.
      </div>
    </section>

    <section class="ai-series">
      <span class="ai-series__label">계열 선택</span>
      <button
        v-for="s in seriesList"
        :key="s.key"
        type="button"
        class="ai-series__btn"
        :class="{ 'is-on': s.key === seriesKey, 'is-off': s.disabled }"
        :disabled="s.disabled"
        :title="s.disabled ? '데이터 없음' : s.processName"
        @click="selectSeries(s.key)"
      >{{ s.name }}<small v-if="s.key === seriesKey && data"> · {{ data.rowCount }}건</small></button>
      <span v-if="loading" class="ai-series__loading">데이터 로딩 중…</span>
      <span v-else-if="loadError" class="ai-series__error">{{ loadError }}</span>
    </section>

    <section v-if="data" class="dv-layout">
      <!-- 왼쪽: 조건 트리 -->
      <aside class="dv-side">
        <DvDeviceFigure
          v-if="deviceLayers"
          :title="deviceTitle"
          :layers="deviceLayers"
          @toggle-value="toggleValue"
          @open-layer="openLayer"
          @clear-layer="clearProps"
          @hover="hoverLayer"
        />
        <div class="dv-side__head">
          <span>조건 필터</span>
          <small>속성 안 OR · 속성 사이 AND</small>
          <button type="button" class="dv-btn" :disabled="!activeFilterChips.length" @click="clearFilters">모두 해제</button>
        </div>
        <label class="dv-side__opt" title="'해당 없음', 'ex) …' 같은 자리표시 값을 조건 목록에서 숨깁니다">
          <input type="checkbox" v-model="hidePlaceholders" /> 자리표시 값(해당 없음·ex) 숨김
        </label>
        <div v-if="activeFilterChips.length" class="dv-active">
          <span v-for="c in activeFilterChips" :key="c.key" class="dv-active__chip" :title="c.text">
            <b>{{ c.prop }}</b> {{ c.short }}
            <button type="button" title="제거" @click="c.remove()">×</button>
          </span>
        </div>
        <DvFilterTree
          ref="tree"
          :categories="categories"
          mode="filter"
          :selected="selected"
          :ranges="ranges"
          :highlight="highlightProps"
          @toggle-value="toggleValue"
          @set-values="setValues"
          @add-range="addRange"
          @remove-range="removeRange"
          @range-change="touchRanges"
        />
      </aside>

      <!-- 오른쪽: 결과 + 출력 -->
      <div class="dv-main">
        <div class="ai-panel dv-table">
          <h3 class="ai-panel__title">
            검색 결과 <small>{{ filteredRows.length }}건 / 전체 {{ data.rows.length }}건<template v-if="checked.length"> · 선택 {{ checked.length }}건</template></small>
            <span class="dv-tools">
              <button type="button" class="dv-btn" :class="{ 'is-on': showColPicker }" @click="showColPicker = !showColPicker">표시 열 {{ columns.length }}/{{ MAX_COLS }}</button>
              <button type="button" class="dv-btn" @click="checkAllVisible">현재 페이지 선택</button>
              <button type="button" class="dv-btn" :disabled="!checked.length" @click="checked = []">선택 해제</button>
              <button type="button" class="dv-btn" :disabled="!filteredRows.length" @click="exportCsv">CSV 내보내기</button>
            </span>
          </h3>
          <div v-if="showColPicker" class="dv-picker">
            <div class="dv-picker__head"><b>표시 열 선택</b><small>분류·노드 체크박스로 하위 전체 선택 · 최대 {{ MAX_COLS }}개</small><button type="button" class="dv-btn" @click="showColPicker = false">닫기</button></div>
            <DvFilterTree :categories="categories" mode="pick" :picked="columns" :pick-max="MAX_COLS" @set-picks="setColumns" />
          </div>
          <div class="dv-table__wrap">
            <table class="dv-grid">
              <thead>
                <tr class="dv-grid__cat">
                  <th colspan="2"></th>
                  <th v-for="g in columnGroups" :key="g.key" :colspan="g.props.length" :class="'dv-cat-' + g.key">{{ g.name }}</th>
                </tr>
                <tr>
                  <th></th>
                  <th>ID</th>
                  <th v-for="p in columnProps" :key="p.id" :title="p.nodeName">{{ p.name }}<small v-if="p.unit"> ({{ p.unit }})</small><em>{{ p.nodeName }}</em></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="r in pageRows" :key="r.id" :class="{ 'is-sel': checkedSet.has(r.id) }" @click="toggleRow(r.id)">
                  <td class="dv-check"><input type="checkbox" :checked="checkedSet.has(r.id)" @click.stop="toggleRow(r.id)" /></td>
                  <td class="dv-id">{{ r.label }}</td>
                  <td v-for="p in columnProps" :key="p.id" :class="{ 'dv-num': p.isNumeric }">{{ cellText(p, r.id) }}</td>
                </tr>
              </tbody>
            </table>
            <p v-if="!filteredRows.length" class="ai-panel__empty">조건에 맞는 행이 없습니다. 조건을 줄여 보세요.</p>
          </div>
          <div v-if="pageCount > 1" class="dv-pager">
            <button type="button" class="dv-btn" :disabled="page === 1" @click="page--">‹</button>
            <span>{{ page }} / {{ pageCount }}</span>
            <button type="button" class="dv-btn" :disabled="page === pageCount" @click="page++">›</button>
          </div>
        </div>

        <div class="ai-panel dv-output">
          <h3 class="ai-panel__title">
            출력 특성 <small>{{ checked.length ? `선택 ${checked.length}건` : `조건에 맞는 ${Math.min(filteredRows.length, OUT_CAP)}건` }} 기준</small>
            <span class="dv-tools">
              <button type="button" class="dv-btn" :class="{ 'is-on': showOutPicker }" @click="showOutPicker = !showOutPicker">출력 항목 {{ outputs.length }}/{{ MAX_OUT }}</button>
            </span>
          </h3>
          <div v-if="showOutPicker" class="dv-picker">
            <div class="dv-picker__head"><b>출력 항목 선택</b><small>숫자 속성만 · 기본은 「성능」 · 최대 {{ MAX_OUT }}개</small><button type="button" class="dv-btn" @click="showOutPicker = false">닫기</button></div>
            <DvFilterTree :categories="categories" mode="pick" :picked="outputs" :pick-max="MAX_OUT" numeric-only @set-picks="setOutputs" />
          </div>

          <template v-if="outputGroups.length && targetRows.length">
            <div v-for="g in outputGroups" :key="g.key" class="dv-outcat">
              <div class="dv-outcat__title" :class="'dv-cat-' + g.key">{{ g.name }}</div>
              <table class="dv-grid dv-summary">
                <thead><tr><th>항목</th><th>노드</th><th>n</th><th>최소</th><th>평균</th><th>최대</th></tr></thead>
                <tbody>
                  <tr v-for="s in g.summary" :key="s.id">
                    <td>{{ s.name }}<small v-if="s.unit"> ({{ s.unit }})</small></td>
                    <td class="dv-dim">{{ s.nodeName }}</td>
                    <td class="dv-num">{{ s.n }}</td>
                    <td class="dv-num">{{ fmt(s.min) }}</td>
                    <td class="dv-num"><b>{{ fmt(s.mean) }}</b></td>
                    <td class="dv-num">{{ fmt(s.max) }}</td>
                  </tr>
                </tbody>
              </table>
              <div class="dv-barwrap">
                <div v-for="b in g.bars" :key="b.id" class="dv-bars">
                  <div class="dv-bars__title">{{ b.name }}<small v-if="b.unit"> ({{ b.unit }})</small><small v-if="b.truncated"> · 상위 {{ MAX_BARS }}건</small></div>
                  <div v-for="item in b.items" :key="item.rowId" class="dv-bar" :class="{ 'is-sel': checkedSet.has(item.rowId) }">
                    <span class="dv-bar__label" :title="item.label">{{ item.label }}</span>
                    <span class="dv-bar__track"><span class="dv-bar__fill" :style="{ width: item.pct + '%' }"></span></span>
                    <span class="dv-bar__val">{{ fmt(item.value) }}</span>
                  </div>
                </div>
              </div>
            </div>
          </template>
          <p v-else class="ai-panel__empty">표시할 출력 항목이나 행이 없습니다.<br />「출력 항목」에서 숫자 속성을 고르거나 조건을 줄여 보세요.</p>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { SERIES, resolveSeries, loadSeries } from "./lib/dvData";
import DvFilterTree from "./DvFilterTree.vue";
import DvDeviceFigure from "./DvDeviceFigure.vue";
import DEVICE_LAYERS from "./lib/dvLayers.m3d.js"; // 소자 단면 층 ↔ 노드/속성 매핑(핵심 재료 5종)
import CATEGORY_TABLE from "./lib/dvCategories.m3d.js"; // docs/dv-search-tree-m3d.md 에서 생성한 분류표(노드·속성 지정, 재료 노드 ← 만든 공정)

/** 4대 분류. 노드(공정 항목)를 태그 유형·이름·속성 분류(property_class)로 배정한다. */
const CATEGORIES = [
  { key: "material", name: "재료" },
  { key: "process", name: "공정" },
  { key: "performance", name: "성능" },
  { key: "analysis", name: "분석" },
  { key: "etc", name: "기본 정보" },
];
const PERF_NAME = /performance|inverter|device/i;
const ANALYSIS_NAME = /xps|ups|ipes|xrd|sem|tem|afm|j-v|i-v|c-v|iv\b|cv\b|electrical|analysis|measure|spectr|ellips|raman|uv|profil|hall|resist|dielectric/i;
// property_class id: 1 Performance, 2 Process, 3 Property, 4 Structure, 5 metadata, 6 chemical information, 7 crystallography
// 노드 분류와 다른 분류로 옮겨진 속성을 담는 가상 노드의 꼬리표(분류별)
const VIRTUAL_TAIL = { material: "(이 공정의 물질)", performance: "(이 측정의 성능값)", analysis: "(이 공정의 분석값)", process: "(이 노드의 공정 조건)" };
const CLASS_TO_CAT = { 1: "performance", 2: "process", 3: "analysis", 4: "analysis", 6: "material", 7: "analysis", 5: "etc" };

function categorize(group, table) {
  // 1순위: 분류표의 노드 지정(사용자 확정)
  const fixed = table && table.nodes && table.nodes[String(group.name || "").trim()];
  if (fixed) return String(fixed).split(":")[0];
  if (!group.id) return "etc";
  const name = String(group.name || "");
  if (PERF_NAME.test(name)) return "performance";
  const t = String(group.tagType || "").toUpperCase();
  if (t === "MATERIAL") return "material";
  if (t === "PROCESS") return "process";
  if (t === "EQUIPMENT") return ANALYSIS_NAME.test(name) ? "analysis" : "process";
  // 태그 유형이 없으면 속성 분류 다수결
  const votes = {};
  group.props.forEach((p) => { const c = CLASS_TO_CAT[p.classId]; if (c) votes[c] = (votes[c] || 0) + 1; });
  const best = Object.keys(votes).sort((a, b) => votes[b] - votes[a])[0];
  return best || "etc";
}

// 자리표시 값: 조건 트리·표 집계에서 기본적으로 숨긴다(토글로 표시 가능)
const PLACEHOLDER = /^(ex\)|예\)|해당\s*없음|example$|-$|n\/a$|na$|none$)/i;

const OUTPUT_HINTS = ["mobility", "threshold", "subthreshold", "on-off", "on/off", "on current", "breakdown", "dielectric constant", "band gap", "capacitance"];

export default {
  name: "DvSearch",
  components: { DvFilterTree, DvDeviceFigure },
  data() {
    return {
      MAX_FACET: 15, MAX_COLS: 14, MAX_OUT: 6, MAX_BARS: 25, OUT_CAP: 200, PAGE: 50,
      seriesKey: null, seriesProc: null, data: null, loading: false, loadError: null,
      selected: {}, ranges: {}, rangeTick: 0,
      columns: [], outputs: [], checked: [], page: 1,
      showColPicker: false, showOutPicker: false,
      hidePlaceholders: true,
      highlightProps: [], // 소자 그림 호버 중인 층의 속성 id
    };
  },
  computed: {
    seriesList() {
      return SERIES.map((s) => ({
        ...s,
        disabled: this.seriesProc ? !this.seriesProc[s.key] : false,
        processName: this.seriesProc && this.seriesProc[s.key] ? this.seriesProc[s.key].processName : "",
      }));
    },
    /** 속성에 트리용 정보(조건 옵션·숫자 범위·노드명·분류)를 붙인 목록 */
    enriched() {
      if (!this.data) return [];
      const out = [];
      const table = CATEGORY_TABLE[String(this.data.processId)] || null;
      this.data.groups.forEach((g) => {
        const cat = categorize(g, table);
        g.props.forEach((p) => {
          // 속성 단위 지정(예: 공정 노드의 물질명 속성 → 재료)이 노드 분류보다 우선
          const ov = table && table.props && table.props[`${String(g.name || "").trim()}/${p.name}`];
          const pcat = ov ? String(ov).split(":")[0] : cat;
          const counts = {};
          Object.values(p.texts || {}).forEach((t) => { if (!(this.hidePlaceholders && PLACEHOLDER.test(String(t).trim()))) counts[t] = (counts[t] || 0) + 1; });
          const keys = Object.keys(counts);
          const isNumeric = p.type === "NUMBER" && p.active;
          const vals = isNumeric ? Object.values(p.values) : [];
          out.push({
            ...p,
            nodeId: g.id, nodeName: g.name, nodeOrder: g.order, nodeCat: cat, cat: pcat,
            isNumeric,
            min: isNumeric ? Math.min(...vals) : null,
            max: isNumeric ? Math.max(...vals) : null,
            isFacet: !isNumeric && p.type !== "IMAGE" && p.type !== "DATE" && keys.length >= 1 && keys.length <= this.MAX_FACET,
            options: keys.map((k) => ({ value: k, count: counts[k] })).sort((a, b) => b.count - a.count),
          });
        });
      });
      return out;
    },
    propMap() { const m = {}; this.enriched.forEach((p) => { m[p.id] = p; }); return m; },
    /** 분류 → 노드 → 속성 트리 */
    categories() {
      const table = CATEGORY_TABLE[String(this.data.processId)] || null;
      const makers = (table && table.makers) || {}; // 재료 노드명 → 만든 공정 노드명
      const madeBy = {}; Object.keys(makers).forEach((mat) => { (madeBy[makers[mat]] = madeBy[makers[mat]] || []).push(mat); });
      return CATEGORIES.map((c) => {
        const nodes = [];
        this.data.groups.forEach((g) => {
          const props = this.enriched.filter((p) => p.nodeId === g.id && p.cat === c.key && (p.textCount > 0));
          if (!props.length) return;
          const nodeCat = props[0].nodeCat;
          if (nodeCat === c.key) {
            nodes.push({ id: g.id, name: g.name, order: g.order, tail: null, props });
          } else {
            // 노드 분류와 다른 속성(예: 공정 노드의 물질명 → 재료): 공정 순서를 유지한 가상 노드로.
            // 분류표에 "이 공정이 만든 재료 노드"가 있으면 그 이름을 쓰고, 없으면 공정명 + 꼬리표.
            const mats = madeBy[String(g.name || "").trim()];
            nodes.push({
              id: `${g.id}:${c.key}`, order: g.order,
              name: mats && mats.length ? mats.join(" / ") : g.name,
              tail: mats && mats.length ? `← ${g.name}` : VIRTUAL_TAIL[c.key] || "(이 노드의 속성)",
              props,
            });
          }
        });
        nodes.sort((a, b) => (a.order == null ? 1e9 : a.order) - (b.order == null ? 1e9 : b.order));
        return { ...c, nodes };
      }).filter((c) => c.nodes.length);
    },
    /** 소자 단면 그림용 층 목록: 매핑(dvLayers)의 sources 를 실데이터 속성으로 풀고 값 분포·필터 상태를 붙인다 */
    deviceTitle() { const d = DEVICE_LAYERS[String(this.data.processId)]; return d ? d.name : ""; },
    deviceLayers() {
      const def = DEVICE_LAYERS[String(this.data.processId)];
      if (!def) return null;
      const norm = (v) => String(v || "").trim().toLowerCase();
      return def.layers.map((l) => {
        const sources = [];
        l.sources.forEach((src) => {
          this.enriched.forEach((p) => {
            if (norm(p.nodeName) !== norm(src.node) || norm(p.name) !== norm(src.prop)) return;
            if (src.order != null && p.nodeOrder !== src.order) return;
            if (!p.isFacet && !(p.options && p.options.length)) return;
            if (!sources.some((x) => x.id === p.id)) sources.push(p);
          });
        });
        const all = [];
        sources.forEach((p) => (p.options || []).forEach((o) => all.push({ pid: p.id, value: o.value, count: o.count, nodeName: p.nodeName, prop: p.name, on: !!(this.selected[p.id] && this.selected[p.id][o.value]) })));
        all.sort((a, b) => b.count - a.count);
        const shown = all.slice(0, 10);
        const filterCount = sources.filter((p) => this.selected[p.id] && Object.keys(this.selected[p.id]).length).length;
        return { ...l, sources, values: shown, more: all.length - shown.length, filterCount, filtered: filterCount > 0 };
      });
    },
    activeFilterChips() {
      const chips = [];
      Object.keys(this.selected).forEach((pid) => {
        const vals = Object.keys(this.selected[pid] || {});
        if (!vals.length) return;
        const p = this.propMap[pid];
        const text = vals.join(" | ");
        chips.push({ key: "s" + pid, prop: p ? p.name : pid, text, short: vals.length > 2 ? `${vals.length}개 값` : text, remove: () => this.$delete(this.selected, pid) });
      });
      // eslint-disable-next-line no-unused-expressions
      this.rangeTick;
      Object.keys(this.ranges).forEach((pid) => {
        const r = this.ranges[pid]; const p = this.propMap[pid];
        const text = `${this.fmt(r.min)} ~ ${this.fmt(r.max)}`;
        chips.push({ key: "r" + pid, prop: p ? p.name : pid, text, short: text, remove: () => this.removeRange(pid) });
      });
      return chips;
    },
    filteredRows() {
      if (!this.data) return [];
      // eslint-disable-next-line no-unused-expressions
      this.rangeTick;
      const sel = Object.keys(this.selected).filter((pid) => Object.keys(this.selected[pid]).length);
      const rng = Object.keys(this.ranges);
      return this.data.rows.filter((r) => {
        for (const pid of sel) {
          const p = this.propMap[pid];
          const t = p && p.texts ? p.texts[r.id] : undefined;
          if (t === undefined || !this.selected[pid][t]) return false;
        }
        for (const pid of rng) {
          const p = this.propMap[pid];
          const v = p && p.values ? p.values[r.id] : undefined;
          const { min, max } = this.ranges[pid];
          if (v === undefined) return false;
          if (min !== "" && min !== null && v < min) return false;
          if (max !== "" && max !== null && v > max) return false;
        }
        return true;
      });
    },
    pageCount() { return Math.max(1, Math.ceil(this.filteredRows.length / this.PAGE)); },
    pageRows() { const s = (this.page - 1) * this.PAGE; return this.filteredRows.slice(s, s + this.PAGE); },
    checkedSet() { return new Set(this.checked); },
    columnProps() {
      // 표시 열은 분류 순서(재료→공정→성능→분석→기본)로 정렬해 그룹 헤더를 만든다
      const order = {}; CATEGORIES.forEach((c, i) => { order[c.key] = i; });
      return this.columns.map((id) => this.propMap[id]).filter(Boolean).sort((a, b) => order[a.cat] - order[b.cat]);
    },
    columnGroups() {
      const groups = [];
      this.columnProps.forEach((p) => {
        const last = groups[groups.length - 1];
        if (last && last.key === p.cat) last.props.push(p);
        else groups.push({ key: p.cat, name: CATEGORIES.find((c) => c.key === p.cat).name, props: [p] });
      });
      return groups;
    },
    outputProps() { return this.outputs.map((id) => this.propMap[id]).filter(Boolean); },
    targetRows() {
      return this.checked.length
        ? this.filteredRows.filter((r) => this.checkedSet.has(r.id))
        : this.filteredRows.slice(0, this.OUT_CAP);
    },
    outputGroups() {
      return CATEGORIES.map((c) => {
        const props = this.outputProps.filter((p) => p.cat === c.key);
        if (!props.length) return null;
        const summary = props.map((p) => {
          const vals = this.targetRows.map((r) => p.values[r.id]).filter((v) => v !== undefined);
          const n = vals.length;
          return { id: p.id, name: p.name, unit: p.unit, nodeName: p.nodeName, n,
            min: n ? Math.min(...vals) : null, max: n ? Math.max(...vals) : null, mean: n ? vals.reduce((a, b) => a + b, 0) / n : null };
        });
        const bars = props.map((p) => {
          const items = this.targetRows.map((r) => ({ rowId: r.id, label: r.label, value: p.values[r.id] })).filter((i) => i.value !== undefined).sort((a, b) => b.value - a.value);
          const truncated = items.length > this.MAX_BARS;
          const shown = items.slice(0, this.MAX_BARS);
          const max = Math.max(...shown.map((i) => Math.abs(i.value)), 0) || 1;
          shown.forEach((i) => { i.pct = Math.max(2, Math.round((Math.abs(i.value) / max) * 100)); });
          return { id: p.id, name: p.name, unit: p.unit, items: shown, truncated };
        });
        return { key: c.key, name: c.name, summary, bars };
      }).filter(Boolean);
    },
  },
  watch: {
    filteredRows() { this.page = 1; },
  },
  async mounted() {
    const q = String(this.$route.query.series || "").toUpperCase();
    this.seriesKey = SERIES.some((s) => s.key === q) ? q : SERIES[0].key;
    try { this.seriesProc = await resolveSeries(this.$wApi); } catch (e) { /* load 에서 처리 */ }
    await this.load();
  },
  methods: {
    fmt(v) {
      if (v === null || v === undefined || v === "" || !isFinite(v)) return "–";
      const a = Math.abs(v);
      if (a !== 0 && (a >= 1e6 || a < 1e-3)) return Number(v).toExponential(2);
      if (a >= 100) return Math.round(v).toLocaleString();
      if (a >= 1) return Number(v.toFixed(2)).toString();
      return Number(v.toFixed(4)).toString();
    },
    async selectSeries(key) {
      if (key === this.seriesKey) return;
      this.seriesKey = key;
      if (String(this.$route.query.series || "").toUpperCase() !== key) {
        this.$router.replace({ query: { ...this.$route.query, series: key } });
      }
      await this.load();
    },
    async load() {
      this.loading = true; this.loadError = null; this.data = null;
      this.selected = {}; this.ranges = {}; this.checked = []; this.columns = []; this.outputs = [];
      try {
        const d = await loadSeries(this.$wApi, this.seriesKey);
        if (!d) { this.loadError = "해당 계열의 공정도를 찾지 못했습니다."; return; }
        this.data = d;
        this.$nextTick(() => {
          // 기본 출력: 성능 분류의 숫자 속성(힌트 이름 우선), 없으면 유효값 많은 숫자 속성
          const nums = this.enriched.filter((p) => p.isNumeric);
          const rank = (p) => OUTPUT_HINTS.findIndex((h) => p.name.toLowerCase().includes(h));
          const perf = nums.filter((p) => p.cat === "performance").sort((a, b) => (rank(a) < 0 ? 99 : rank(a)) - (rank(b) < 0 ? 99 : rank(b)) || b.validCount - a.validCount);
          const pool = perf.length ? perf : nums.sort((a, b) => b.validCount - a.validCount);
          this.outputs = pool.slice(0, 3).map((p) => p.id);
          // 기본 표시 열: 재료·공정에서 값 많은 조건 속성 2개씩 + 출력
          const pick = (cat, n) => this.enriched.filter((p) => p.cat === cat && p.isFacet).sort((a, b) => b.textCount - a.textCount).slice(0, n).map((p) => p.id);
          this.columns = [...new Set([...pick("material", 2), ...pick("process", 3), ...this.outputs])].slice(0, this.MAX_COLS);
        });
      } catch (e) {
        this.loadError = "데이터 로딩 실패: " + ((e && e.message) || e);
      } finally {
        this.loading = false;
      }
    },
    // ─── 필터 ───
    toggleValue(pid, value) {
      const cur = { ...(this.selected[pid] || {}) };
      if (cur[value]) delete cur[value]; else cur[value] = true;
      if (Object.keys(cur).length) this.$set(this.selected, pid, cur); else this.$delete(this.selected, pid);
    },
    setValues(pid, values) {
      if (!values || !values.length) { this.$delete(this.selected, pid); return; }
      const cur = {}; values.forEach((v) => { cur[v] = true; });
      this.$set(this.selected, pid, cur);
    },
    addRange(p) { if (!this.ranges[p.id]) this.$set(this.ranges, p.id, { min: p.min, max: p.max }); },
    removeRange(pid) { this.$delete(this.ranges, pid); },
    touchRanges() { this.rangeTick++; },
    clearFilters() { this.selected = {}; this.ranges = {}; },
    clearProps(pids) { (pids || []).forEach((pid) => { this.$delete(this.selected, pid); this.$delete(this.ranges, pid); }); },
    hoverLayer(key) {
      const l = key && this.deviceLayers ? this.deviceLayers.find((x) => x.key === key) : null;
      this.highlightProps = l ? l.sources.map((p) => p.id) : [];
    },
    /** 그림의 층 클릭 → 트리에서 해당 속성들을 펼치고 첫 항목으로 스크롤 */
    openLayer(key, pids) {
      const tree = this.$refs.tree;
      if (!tree || !pids || !pids.length) return;
      tree.openProps(pids);
      this.$nextTick(() => {
        const el = tree.$el.querySelector(`[data-pid="${pids[0]}"]`);
        if (el && el.scrollIntoView) el.scrollIntoView({ block: "center", behavior: "smooth" });
      });
    },
    // ─── 표 ───
    setColumns(ids, on) {
      const s = new Set(this.columns);
      if (on) { for (const id of ids) { if (s.size >= this.MAX_COLS) break; s.add(id); } }
      else ids.forEach((id) => s.delete(id));
      this.columns = [...s];
    },
    setOutputs(ids, on) {
      const s = new Set(this.outputs);
      if (on) { for (const id of ids) { if (s.size >= this.MAX_OUT) break; s.add(id); } }
      else ids.forEach((id) => s.delete(id));
      this.outputs = [...s];
    },
    toggleRow(id) { const i = this.checked.indexOf(id); if (i >= 0) this.checked.splice(i, 1); else this.checked.push(id); },
    checkAllVisible() { const s = new Set(this.checked); this.pageRows.forEach((r) => s.add(r.id)); this.checked = [...s]; },
    cellText(p, rowId) {
      if (p.isNumeric && p.values[rowId] !== undefined) return this.fmt(p.values[rowId]);
      const t = p.texts ? p.texts[rowId] : undefined;
      return t === undefined ? "" : t;
    },
    exportCsv() {
      const cols = this.columnProps;
      const esc = (v) => '"' + String(v === undefined || v === null ? "" : v).replace(/"/g, '""') + '"';
      const head1 = ["", "", ...this.columnGroups.flatMap((g) => g.props.map((_, i) => (i === 0 ? g.name : "")))].map(esc).join(",");
      const head2 = ["ID", "", ...cols.map((p) => p.name + (p.unit ? ` (${p.unit})` : "") + ` [${p.nodeName}]`)].map(esc).join(",");
      const lines = this.filteredRows.map((r) => [r.label, "", ...cols.map((p) => (p.texts && p.texts[r.id]) || "")].map(esc).join(","));
      const blob = new Blob(["﻿" + [head1, head2, ...lines].join("\r\n")], { type: "text/csv;charset=utf-8" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${this.data.seriesName}_검색결과_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
    },
  },
};
</script>

<style lang="scss" scoped>
.dv-search { max-width: 1500px; margin: 0 auto; padding: 40px 20px 100px; }
.ai-page-head {
  margin-bottom: 18px;
  &__crumb { font-size: 13px; color: #789; a { color: #0a93b5; text-decoration: none; } span { margin: 0 6px; } em { font-style: normal; color: #334; font-weight: 600; } }
  &__title { margin-top: 6px; font-size: 26px; font-weight: 700; color: #111; letter-spacing: -0.5px; }
  &__sub { margin-top: 8px; font-size: 14px; color: #556; line-height: 22px; b { color: #003f67; } }
}
.ai-data-banner { margin-top: 12px; padding: 10px 14px; border-radius: 8px; background: #eef7fb; border: 1px solid #d4e9f2; font-size: 13px; color: #334; b { color: #003f67; } }
.ai-series {
  display: flex; flex-wrap: wrap; align-items: center; gap: 8px; margin: 14px 0 16px;
  &__label { font-size: 13px; font-weight: 700; color: #334; margin-right: 4px; }
  &__btn { padding: 7px 14px; border: 1px solid #dde6ec; border-radius: 999px; background: #fff; font-size: 13px; color: #334; cursor: pointer;
    small { color: #789; margin-left: 2px; }
    &.is-on { background: #003f67; border-color: #003f67; color: #fff; small { color: #c7d8e4; } }
    &.is-off { color: #aab; background: #f6f8fa; cursor: not-allowed; } }
  &__loading { font-size: 13px; color: #0a93b5; }
  &__error { font-size: 13px; color: #c0392b; }
}
.dv-btn { padding: 4px 10px; border: 1px solid #dde6ec; border-radius: 6px; background: #fff; font-size: 12px; color: #334; cursor: pointer; white-space: nowrap;
  &:disabled { color: #aab; cursor: not-allowed; } &.is-on { background: #003f67; border-color: #003f67; color: #fff; } }

.dv-layout { display: grid; grid-template-columns: 320px minmax(0, 1fr); gap: 18px; align-items: start; }
@media (max-width: 1000px) { .dv-layout { grid-template-columns: 1fr; } }
.dv-side { position: sticky; top: 80px; max-height: calc(100vh - 100px); overflow-y: auto; padding-right: 2px;
  &__head { display: flex; align-items: center; gap: 8px; margin-bottom: 8px; font-size: 13px; font-weight: 700; color: #334; small { color: #789; font-weight: 400; font-size: 11px; flex: 1 1 auto; } }
  &__opt { display: flex; align-items: center; gap: 6px; margin: -2px 0 8px; font-size: 12px; color: #556; cursor: pointer; input { margin: 0; accent-color: #003f67; } }
}
.dv-active { display: flex; flex-wrap: wrap; gap: 4px; margin-bottom: 8px;
  &__chip { display: inline-flex; align-items: center; gap: 5px; padding: 2px 7px 2px 9px; border-radius: 999px; background: #003f67; color: #fff; font-size: 11.5px; max-width: 100%;
    b { font-weight: 600; color: #c7d8e4; white-space: nowrap; }
    button { border: 0; background: none; color: #fff; cursor: pointer; font-size: 13px; line-height: 1; padding: 0; } }
}
.dv-main { display: flex; flex-direction: column; gap: 18px; min-width: 0; }
.ai-panel { border: 1px solid #dde6ec; border-radius: 12px; background: #fff; padding: 14px 16px 16px;
  &__title { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; font-size: 16px; font-weight: 700; color: #111; margin-bottom: 10px; small { font-size: 12px; color: #789; font-weight: 400; } }
  &__empty { padding: 30px 0; text-align: center; font-size: 13px; color: #789; line-height: 20px; }
}
.dv-tools { margin-left: auto; display: flex; flex-wrap: wrap; gap: 6px; }
.dv-picker { margin: 0 0 12px; padding: 10px; border: 1px solid #0dbde9; border-radius: 10px; background: #f6fbfe; max-height: 420px; overflow-y: auto;
  &__head { display: flex; align-items: center; gap: 8px; margin-bottom: 8px; font-size: 13px; b { color: #003f67; } small { color: #789; flex: 1 1 auto; } } }
.dv-table__wrap { overflow-x: auto; }
.dv-grid { width: 100%; border-collapse: collapse; font-size: 12.5px;
  th { text-align: left; padding: 6px 8px; background: #f3f6f9; color: #334; font-weight: 700; border-bottom: 1px solid #dde6ec; white-space: nowrap; vertical-align: bottom;
    small { color: #789; font-weight: 400; } em { display: block; font-style: normal; font-size: 10.5px; color: #789; font-weight: 400; } }
  &__cat th { text-align: center; font-size: 11.5px; letter-spacing: 0.3px; border-bottom: 2px solid #fff; padding: 4px 8px; }
  td { padding: 6px 8px; border-bottom: 1px solid #eef2f5; color: #223; vertical-align: top; max-width: 220px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  tbody tr { cursor: pointer; } tbody tr:hover { background: #f6fbfe; } tbody tr.is-sel { background: #e8f6fb; }
}
.dv-cat-material { background: #e9f3ea !important; color: #2e6b3a !important; }
.dv-cat-process { background: #e8f1fb !important; color: #1f4f8a !important; }
.dv-cat-performance { background: #fdeeea !important; color: #a3402d !important; }
.dv-cat-analysis { background: #f1ecfa !important; color: #5a3d9c !important; }
.dv-cat-etc { background: #f3f3f3 !important; color: #555 !important; }
.dv-num { text-align: right; font-variant-numeric: tabular-nums; }
.dv-dim { color: #789; }
.dv-check { width: 28px; input { accent-color: #003f67; } }
.dv-id { font-weight: 600; color: #003f67; white-space: nowrap; }
.dv-pager { display: flex; justify-content: center; align-items: center; gap: 10px; margin-top: 10px; font-size: 12px; color: #334; }
.dv-outcat { margin-top: 8px; padding-top: 8px; border-top: 1px solid #eef2f5;
  &__title { display: inline-block; padding: 3px 10px; border-radius: 6px; font-size: 12px; font-weight: 700; margin-bottom: 8px; } }
.dv-summary { margin-bottom: 10px; td, th { white-space: nowrap; } }
.dv-barwrap { display: grid; grid-template-columns: repeat(auto-fit, minmax(300px, 1fr)); gap: 12px 24px; }
.dv-bars { &__title { font-size: 12.5px; font-weight: 700; color: #334; margin-bottom: 4px; small { color: #789; font-weight: 400; } } }
.dv-bar { display: grid; grid-template-columns: 90px 1fr 70px; align-items: center; gap: 8px; padding: 2px 0; font-size: 12px;
  &__label { color: #556; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  &__track { height: 10px; background: #eef2f5; border-radius: 5px; overflow: hidden; }
  &__fill { display: block; height: 100%; background: linear-gradient(90deg, #0dbde9, #003f67); border-radius: 5px; }
  &__val { text-align: right; color: #223; font-variant-numeric: tabular-nums; }
  &.is-sel &__label { color: #003f67; font-weight: 600; }
}
</style>
